import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "../i18n/LanguageContext";
import Seo from "../components/Seo";
import {
  ArrowRight,
  CheckCircle,
  ChartBar,
  Camera,
  ClipboardText,
  Sparkle,
  EnvelopeSimple,
  Eye,
  Package,
  UsersThree,
  Warning,
} from "@phosphor-icons/react";

const Eyebrow = ({ children, accent = false, light = false }) => (
  <span className={`gm-eyebrow ${accent ? "text-[#0A4DFF]" : light ? "text-white/50" : ""}`}>{children}</span>
);

const Hero = () => {
  const { t } = useLang();
  const s = t.merchandising.hero;
  return (
    <section className="relative bg-white border-b border-[#e5e5e5]" data-testid="merch-hero">
      <div className="absolute inset-0 gm-grid-bg pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-14 md:pb-20 relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-9">
            <Eyebrow accent>{s.eyebrow}</Eyebrow>
            <h1 className="font-display text-5xl md:text-7xl tracking-tighter font-medium leading-[0.95] mt-6 max-w-4xl">
              {s.title}
            </h1>
            <p className="text-lg md:text-xl text-[#525252] mt-8 max-w-3xl leading-relaxed">{s.sub}</p>
            <p className="text-base text-[#0a0a0a] mt-6 max-w-3xl leading-relaxed border-l-2 border-[#0A4DFF] pl-5">
              {s.leadIn}
            </p>
          </div>
          <div className="lg:col-span-3 flex lg:items-end">
            <div className="bg-[#0A4DFF] text-white p-6 w-full">
              <div className="font-mono text-[10px] tracking-widest text-white/70">// COVERAGE</div>
              <div className="font-display text-3xl md:text-4xl tracking-tight mt-3 leading-tight">CZ + SK</div>
              <div className="text-sm text-white/80 mt-3 leading-relaxed">Všechny obchodní řetězce a nezávislý trh.</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Process = () => {
  const { t } = useLang();
  const s = t.merchandising.process;
  return (
    <section className="py-14 md:py-20 bg-[#0a0a0a] text-white" data-testid="merch-process">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
          <div className="md:col-span-7">
            <span className="font-mono uppercase tracking-[0.22em] text-[13px] font-medium text-[#5B82FF]">{s.eyebrow}</span>
            <h2 className="font-display text-3xl md:text-5xl tracking-tight mt-4 leading-[1.05] font-medium">{s.title}</h2>
          </div>
          <p className="md:col-span-5 text-white/70 leading-relaxed">{s.intro}</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 mt-12 border border-white/10">
          {s.steps.map((p, i) => (
            <div key={i} className="p-6 md:p-7 border-b md:border-b-0 last:border-b-0 border-white/10 md:border-r last:md:border-r-0 relative">
              <div className="font-display text-5xl tracking-tighter text-white/35">{p.n}</div>
              <h3 className="font-display text-lg md:text-xl mt-4 leading-tight">{p.h}</h3>
              <p className="text-sm text-white/60 mt-3 leading-relaxed">{p.t}</p>
            </div>
          ))}
        </div>
        <p className="text-base md:text-lg text-white/80 mt-8 max-w-2xl leading-relaxed border-l-2 border-[#0A4DFF] pl-5">
          {s.outro}
        </p>
      </div>
    </section>
  );
};

const Team = () => {
  const { t } = useLang();
  const s = t.merchandising.team;
  return (
    <section className="py-14 md:py-20 bg-white border-b border-[#e5e5e5]" data-testid="merch-team">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
          <div className="md:col-span-7">
            <div className="flex items-center gap-3">
              <UsersThree size={22} weight="thin" className="text-[#0A4DFF]" />
              <Eyebrow>{s.eyebrow}</Eyebrow>
            </div>
            <h2 className="font-display text-3xl md:text-5xl tracking-tight mt-4 leading-[1.05] font-medium">{s.title}</h2>
          </div>
          <p className="md:col-span-5 text-[#525252] leading-relaxed">{s.intro}</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-10">
          {s.roles.map((r, i) => (
            <div key={i} className={`p-7 md:p-9 border ${i === 0 ? "bg-[#0a0a0a] text-white border-[#0a0a0a]" : "bg-[#fafafa] border-[#e5e5e5]"}`}>
              <div className={`font-mono text-[10px] tracking-widest ${i === 0 ? "text-[#5B82FF]" : "text-[#0A4DFF]"}`}>
                {r.kicker}
              </div>
              <h3 className="font-display text-2xl md:text-3xl mt-4 leading-tight">{r.h}</h3>
              <ul className={`mt-6 border-t ${i === 0 ? "border-white/15" : "border-[#0a0a0a]"} divide-y ${i === 0 ? "divide-white/10" : "divide-[#e5e5e5]"}`}>
                {r.items.map((it, j) => (
                  <li key={j} className="py-3 flex items-start gap-3 text-sm leading-relaxed">
                    <CheckCircle size={16} weight="thin" className={`shrink-0 mt-0.5 ${i === 0 ? "text-[#5B82FF]" : "text-[#0A4DFF]"}`} />
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const Quality = () => {
  const { t } = useLang();
  const s = t.merchandising.quality;
  return (
    <section className="py-14 md:py-20 bg-[#fafafa]" data-testid="merch-quality">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-start">
          {/* LEFT — heading + body */}
          <div className="md:col-span-8">
            <div className="flex items-center gap-3">
              <Eye size={22} weight="thin" className="text-[#0A4DFF]" />
              <Eyebrow>{s.eyebrow}</Eyebrow>
            </div>
            <h2 className="font-display text-3xl md:text-5xl tracking-tight mt-4 leading-[1.05] font-medium max-w-2xl">
              {s.title}
            </h2>
            <div className="space-y-5 mt-8 max-w-2xl">
              {s.body.map((p, i) => (
                <p key={i} className="text-[#0a0a0a] leading-relaxed">{p}</p>
              ))}
            </div>
          </div>

          {/* RIGHT — narrow portrait photo */}
          <figure
            className="md:col-span-4 border border-[#e5e5e5] bg-[#0a0a0a] overflow-hidden"
            data-testid="merch-quality-photo"
          >
            <img
              src="/merchandising/aisle-audit.jpg?v=4"
              alt="Pohled do uličky supermarketu IGA — plně doplněné regály a chladicí cabinety"
              loading="lazy"
              className="block w-full h-auto"
            />
          </figure>
        </div>

        {/* KPI callouts row, under both columns */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-12">
          {s.callouts.map((c, i) => (
            <div
              key={i}
              className="bg-white border border-[#e5e5e5] p-5 flex items-baseline justify-between gap-4"
            >
              <span className="font-display text-2xl md:text-3xl tracking-tight text-[#0A4DFF]">{c.k}</span>
              <span className="text-sm text-[#525252] text-right leading-tight">{c.t}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const reportIcon = (i) => {
  switch (i) {
    case 0:
      return ClipboardText;
    case 1:
      return EnvelopeSimple;
    case 2:
      return ChartBar;
    case 3:
      return Sparkle;
    default:
      return ClipboardText;
  }
};

const Reporting = () => {
  const { t } = useLang();
  const s = t.merchandising.reporting;
  return (
    <section className="py-14 md:py-20 bg-white border-y border-[#e5e5e5]" data-testid="merch-reporting">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
          <div className="md:col-span-7">
            <Eyebrow accent>{s.eyebrow}</Eyebrow>
            <h2 className="font-display text-3xl md:text-5xl tracking-tight mt-4 leading-[1.05] font-medium">{s.title}</h2>
          </div>
          <p className="md:col-span-5 text-[#525252] leading-relaxed">{s.intro}</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-10">
          {s.items.map((it, i) => {
            const Icon = reportIcon(i);
            const isAi = i === 3;
            return (
              <div
                key={i}
                className={`p-7 md:p-9 border ${isAi ? "bg-[#0A4DFF] text-white border-[#0A4DFF]" : "bg-[#fafafa] border-[#e5e5e5]"}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <span className={`font-mono text-[10px] tracking-widest uppercase ${isAi ? "text-white/70" : "text-[#525252]"}`}>{it.tag}</span>
                    <h3 className="font-display text-2xl md:text-3xl tracking-tight mt-3 leading-tight">{it.h}</h3>
                  </div>
                  <Icon size={28} weight="thin" className={isAi ? "text-white" : "text-[#0a0a0a]"} />
                </div>
                <p className={`text-sm md:text-[15px] leading-relaxed mt-5 ${isAi ? "text-white/85" : "text-[#525252]"}`}>{it.body}</p>
                {it.list && (
                  <ul className={`mt-5 border-t pt-4 ${isAi ? "border-white/15" : "border-[#0a0a0a]"} space-y-2`}>
                    {it.list.map((l, j) => (
                      <li key={j} className={`text-sm flex items-start gap-3 leading-relaxed ${isAi ? "text-white/90" : ""}`}>
                        <span className={isAi ? "text-white/60" : "text-[#0A4DFF]"}>·</span>
                        <span>{l}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
        <p className="text-sm md:text-base text-[#525252] italic leading-relaxed mt-8 max-w-3xl">{s.footer}</p>
      </div>
    </section>
  );
};

const Symes = () => {
  const { t } = useLang();
  const s = t.merchandising.symes;
  return (
    <section className="py-14 md:py-20 bg-white border-t border-[#e5e5e5]" data-testid="merch-symes">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Intro */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
          <div className="md:col-span-7">
            <div className="flex items-center gap-3">
              <Sparkle size={22} weight="thin" className="text-[#0A4DFF]" />
              <Eyebrow accent>{s.eyebrow}</Eyebrow>
            </div>
            <h2 className="font-display text-3xl md:text-5xl tracking-tight mt-4 leading-[1.05] font-medium">
              {s.title}
            </h2>
          </div>
          <p className="md:col-span-5 text-[#525252] leading-relaxed">{s.lead}</p>
        </div>

        {/* Showcase — SYMES Standard report (image + sidebar items) */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mt-14 items-start">
          <figure
            className="md:col-span-7 border border-[#e5e5e5] bg-[#f7f7f7] p-3 overflow-hidden"
            data-testid="merch-symes-standard"
          >
            <img
              src="/symes/symes-standard.jpg"
              alt="SYMES Online — standardní reporting návštěvy prodejny"
              loading="lazy"
              className="block w-full h-auto"
            />
            <figcaption className="flex items-center justify-between gap-4 px-2 py-3 mt-1 border-t border-[#e5e5e5] font-mono text-[10px] tracking-widest uppercase text-[#525252]">
              <span>{s.standard.caption}</span>
              <span className="text-[#0A4DFF]">SYMES · STANDARD</span>
            </figcaption>
          </figure>
          <div className="md:col-span-5">
            <span className="gm-eyebrow text-[#0A4DFF]">{s.standard.eyebrow}</span>
            <h3 className="font-display text-2xl md:text-3xl tracking-tight mt-3 leading-tight">
              {s.standard.h}
            </h3>
            <ul className="mt-6 space-y-0">
              {s.standard.items.map((it, i) => (
                <li key={i} className="flex items-start gap-3 py-3 border-b border-[#e5e5e5] text-sm leading-relaxed">
                  <span className="font-mono text-[10px] tracking-widest text-[#0A4DFF] shrink-0 mt-0.5 w-7">{`A.${(i + 1).toString().padStart(2, "0")}`}</span>
                  <span>{it}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Showcase — QlikView */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mt-16 items-start">
          <figure
            className="md:col-span-7 border border-[#0a0a0a] bg-[#0a0a0a] p-3 overflow-hidden"
            data-testid="merch-symes-qlik"
          >
            <img
              src="/symes/qlikview.jpg"
              alt="QlikView analytická platforma — grafická analýza zdrojových dat ze SYMES"
              loading="lazy"
              className="block w-full h-auto"
            />
            <figcaption className="flex items-center justify-between gap-4 px-2 py-3 mt-1 border-t border-white/15 font-mono text-[10px] tracking-widest uppercase text-white/60">
              <span>{s.qlik.caption}</span>
              <span className="text-[#5B82FF]">QLIKVIEW · ANALYTICS</span>
            </figcaption>
          </figure>
          <div className="md:col-span-5">
            <span className="gm-eyebrow text-[#0A4DFF]">{s.qlik.eyebrow}</span>
            <h3 className="font-display text-2xl md:text-3xl tracking-tight mt-3 leading-tight">
              {s.qlik.h}
            </h3>
            <ul className="mt-6 space-y-0">
              {s.qlik.items.map((it, i) => (
                <li key={i} className="flex items-start gap-3 py-3 border-b border-[#e5e5e5] text-sm leading-relaxed">
                  <span className="font-mono text-[10px] tracking-widest text-[#0A4DFF] shrink-0 mt-0.5 w-7">{`Q.${(i + 1).toString().padStart(2, "0")}`}</span>
                  <span>{it}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {s.footer && (
          <p className="text-sm md:text-base text-[#525252] italic leading-relaxed mt-12 max-w-3xl border-l-2 border-[#0A4DFF] pl-5">
            {s.footer}
          </p>
        )}
      </div>
    </section>
  );
};



const Kpis = () => {
  const { t } = useLang();
  const s = t.merchandising.kpis;
  return (
    <section className="py-14 md:py-20 bg-[#fafafa]" data-testid="merch-kpis">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
          <div className="md:col-span-7">
            <div className="flex items-center gap-3">
              <ChartBar size={22} weight="thin" className="text-[#0A4DFF]" />
              <Eyebrow>{s.eyebrow}</Eyebrow>
            </div>
            <h2 className="font-display text-3xl md:text-5xl tracking-tight mt-4 leading-[1.05] font-medium">{s.title}</h2>
          </div>
          <p className="md:col-span-5 text-[#525252] leading-relaxed">{s.intro}</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-10">
          {s.items.map((it, i) => (
            <div key={i} className="bg-white border border-[#e5e5e5] p-7">
              <span className="font-mono text-[10px] tracking-widest text-[#525252]">{`K.0${i + 1}`}</span>
              <h3 className="font-display text-xl md:text-2xl tracking-tight mt-4 leading-tight">{it.h}</h3>
              <p className="text-sm text-[#525252] mt-3 leading-relaxed">{it.t}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const Promo = () => {
  const { t } = useLang();
  const s = t.merchandising.promo;
  return (
    <section className="py-14 md:py-20 bg-white border-y border-[#e5e5e5]" data-testid="merch-promo">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-8 items-start">
        <div className="md:col-span-5">
          <div className="flex items-center gap-3">
            <Camera size={22} weight="thin" className="text-[#0A4DFF]" />
            <Eyebrow>{s.eyebrow}</Eyebrow>
          </div>
          <h2 className="font-display text-3xl md:text-5xl tracking-tight mt-4 leading-[1.05] font-medium">{s.title}</h2>
        </div>
        <ul className="md:col-span-7 border-t border-[#0a0a0a]">
          {s.items.map((it, i) => (
            <li key={i} className="py-5 flex items-start gap-4 border-b border-[#e5e5e5] text-base leading-relaxed">
              <span className="font-mono text-xs text-[#0A4DFF] mt-1.5 shrink-0">{`P.0${i + 1}`}</span>
              <span>{it}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};

const Onboarding = () => {
  const { t } = useLang();
  const s = t.merchandising.onboarding;
  return (
    <section className="py-14 md:py-20 bg-[#0a0a0a] text-white" data-testid="merch-onboarding">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-4xl">
          <div className="flex items-center gap-3">
            <Package size={22} weight="thin" className="text-[#0A4DFF]" />
            <span className="font-mono uppercase tracking-[0.22em] text-[13px] font-medium text-[#5B82FF]">{s.eyebrow}</span>
          </div>
          <h2 className="font-display text-3xl md:text-5xl tracking-tight mt-4 leading-[1.05] font-medium">{s.title}</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mt-10">
          <div className="md:col-span-7 space-y-5">
            {s.body.map((p, i) => (
              <p key={i} className="text-white/80 leading-relaxed">{p}</p>
            ))}
          </div>
          <div className="md:col-span-5">
            <div className="border-l-2 border-[#0A4DFF] pl-5 py-4">
              <span className="gm-eyebrow text-[#5B82FF]">// PROMISE</span>
              <p className="font-display text-2xl md:text-3xl mt-3 leading-tight">{s.promise}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Cta = () => {
  const { t } = useLang();
  const s = t.merchandising.cta;
  return (
    <section className="py-14 md:py-20 bg-white border-t border-[#e5e5e5]" data-testid="merch-cta">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <p className="text-lg md:text-xl text-[#0a0a0a] leading-relaxed max-w-4xl border-l-2 border-[#0A4DFF] pl-6">
          {s.body}
        </p>
      </div>
    </section>
  );
};

const FinalCta = () => {
  const { t } = useLang();
  const s = t.merchandising.finalCta;
  return (
    <section className="py-14 md:py-20 bg-[#0a0a0a] text-white" data-testid="merch-final-cta">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <span className="font-mono uppercase tracking-[0.22em] text-[12px] text-[#5B82FF]">{s.eyebrow}</span>
        <h2 className="font-display text-4xl md:text-6xl tracking-tighter mt-5 leading-[0.95] font-medium max-w-4xl">
          {s.title}
        </h2>
        <p className="text-white/80 mt-5 leading-relaxed max-w-2xl text-base md:text-lg">{s.sub}</p>
        <div className="mt-8 flex flex-wrap items-center gap-4">
          <Link to="/kontakt" className="gm-btn gm-btn-primary" data-testid="merch-final-cta-kontakt">
            {s.button} <ArrowRight size={16} />
          </Link>
          <a
            href={`mailto:${s.email}`}
            className="text-sm font-mono tracking-widest uppercase text-white border-b border-white/30 pb-1 hover:border-white"
            data-testid="merch-final-cta-email"
          >
            {s.email}
          </a>
        </div>
      </div>
    </section>
  );
};


export default function Merchandising() {
  return (
    <div data-testid="merchandising-page">
      <Seo
        title={{ cs: "Merchandising Services", en: "Merchandising Services" }}
        description={{
          cs:
            "Komplexní merchandising napříč CZ a SK. MERVIK + SYMES + AI report. Same-day řešení reklamací, fotodokumentace, KPI a SLA.",
          en:
            "Full-scope merchandising across CZ and SK. MERVIK + SYMES + AI report. Same-day issue resolution, photo proof, KPIs and SLA.",
        }}
        path="/merchandising-services"
      />
      <Hero />
      <Process />
      <Team />
      <Quality />
      <Reporting />
      <Symes />
      <Kpis />
      <Promo />
      <Onboarding />
      <Cta />
      <FinalCta />
    </div>
  );
}
